// ink-component
import React, { useEffect, useRef, useState, ChangeEvent } from 'react';
import NewInput, { NewInputProps } from '../NewInput/NewInput';

export type SensitiveInputProps = {
  /** Value of the input */
  value: string;
  /** onChange callback, sends the event */
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  /** Number of characters from the end of the input that are kept visible when input out of focus */
  lastNVisibleChars?: number;
  /** Character that replaces the obfuscated text */
  replacementChar?: string;
  /** HTML type modifier */
  inputType?: 'text' | 'number';
} & Omit<NewInputProps, 'onChange' | 'value' | 'mask' | 'maxValue' | 'inputType' | 'inputRef' | 'type'>;

/**
 * @purpose Allows users to input non-password, sensitive data
 * @categories form
 * @status beta
 */
function SensitiveInput({ replacementChar, value, lastNVisibleChars = 4, ...props }: SensitiveInputProps) {
  const ref = useRef<HTMLInputElement>(null);
  const [focused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!ref.current) return;

    const input = ref.current;
    const handleFocus = () => setIsFocused(true);
    const handleBlur = () => setIsFocused(false);

    input.addEventListener('focus', handleFocus);
    input.addEventListener('blur', handleBlur);

    return () => {
      input.removeEventListener('focus', handleFocus);
      input.removeEventListener('blur', handleBlur);
    };
  }, [ref]);

  return (
    <>
      <NewInput
        {...props}
        data-private
        value={focused ? value : value.slice(-lastNVisibleChars).padStart(value.length, replacementChar)}
        externalRef={ref}
      />
    </>
  );
}

SensitiveInput.defaultProps = {
  ...NewInput.defaultProps,
  replacementChar: '*',
};

SensitiveInput.displayName = 'SensitiveInput';

export default SensitiveInput;
